import { useRef, useEffect } from 'react';
import { Loader2, ArrowDown, ArrowUp } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import useAnalyticsStore from '../stores/analyticsStore';

// Virtualized analytics event list ([6.5]). Windows the loaded rows with
// @tanstack/react-virtual so the DOM stays light at hundreds+ of rows, and
// auto-loads the next keyset page (store.fetchMore) when scrolling nears the
// end. A visible "Load more" button remains as an accessible fallback.

const ROW_HEIGHT = 40;
const PREFETCH_ROWS = 8; // start the next-page fetch this many rows from the end

function formatTimestamp(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString();
}

const AnalyticsEventList = () => {
  const events = useAnalyticsStore((s) => s.events);
  const hasMore = useAnalyticsStore((s) => s.hasMore);
  const loading = useAnalyticsStore((s) => s.loading);
  const fetchMore = useAnalyticsStore((s) => s.fetchMore);
  const sort = useAnalyticsStore((s) => s.sort);
  const setSort = useAnalyticsStore((s) => s.setSort);
  const selectedEventId = useAnalyticsStore((s) => s.selectedEventId);
  const selectEvent = useAnalyticsStore((s) => s.selectEvent);
  const cameraLabel = useAnalyticsStore((s) => s.cameraLabel);
  // subscribe so the Camera column re-resolves once cameraNames loads
  const cameraNames = useAnalyticsStore((s) => s.cameraNames);
  void cameraNames;

  const scrollRef = useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: events.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  // Infinite scroll: when the last rendered row nears the end of the loaded set,
  // pull the next page. fetchMore is a no-op without hasMore / while loading.
  useEffect(() => {
    const last = virtualItems[virtualItems.length - 1];
    if (!last) return;
    if (hasMore && !loading && last.index >= events.length - 1 - PREFETCH_ROWS) {
      fetchMore();
    }
  }, [virtualItems, events.length, hasMore, loading, fetchMore]);

  return (
    <div className="analytics-list">
      <div className="analytics-list-header analytics-list-row">
        <button
          type="button"
          className="analytics-sort-btn"
          onClick={() => setSort(sort === 'desc' ? 'asc' : 'desc')}
          title={sort === 'desc' ? 'Newest first (click for oldest first)' : 'Oldest first (click for newest first)'}
        >
          <span>Received</span>
          {sort === 'desc' ? <ArrowDown size={13} /> : <ArrowUp size={13} />}
        </button>
        <span>Camera</span>
        <span>Topic</span>
        <span>Vendor</span>
        <span>Category</span>
        <span>Value</span>
      </div>

      <div className="analytics-list-scroll" ref={scrollRef}>
        <div
          className="analytics-list-inner"
          style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
        >
          {virtualItems.map((vi) => {
            const ev = events[vi.index];
            if (!ev) return null;
            return (
              <div
                key={ev.id}
                className={
                  'analytics-list-row analytics-list-data-row' +
                  (ev.id === selectedEventId ? ' selected' : '')
                }
                style={{ transform: `translateY(${vi.start}px)`, height: `${vi.size}px` }}
                onClick={() => selectEvent(ev.id)}
              >
                <span className="analytics-cell-time">{formatTimestamp(ev.receivedAt)}</span>
                <span className="analytics-cell-mono">{cameraLabel(ev.cameraId) || '-'}</span>
                <span className="analytics-cell-mono">{ev.canonicalTopic || '-'}</span>
                <span>{ev.vendor || '-'}</span>
                <span>{ev.eventCategory || '-'}</span>
                <span>{ev.dataValue ?? ev.sourceValue ?? '-'}</span>
              </div>
            );
          })}
        </div>
      </div>

      {loading && (
        <div className="analytics-loading">
          <Loader2 size={20} className="analytics-spin" />
          <span>Loading events...</span>
        </div>
      )}

      {hasMore && !loading && (
        <div className="analytics-load-more">
          <button type="button" className="analytics-load-more-btn" onClick={fetchMore}>
            Load more
          </button>
        </div>
      )}
    </div>
  );
};

export default AnalyticsEventList;
